import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Copy, RefreshCw, ThumbsUp, ThumbsDown, GitBranch, Pencil, MoreHorizontal,
  Trash2, Archive, Download, Share2, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, streamChat } from "@/lib/api";
import { BrandMark } from "@/components/BrandLogo";
import Composer from "@/components/Composer";
import Markdown from "@/components/Markdown";

const STARTERS = [
  "Write a professional email",
  "Research a company or topic",
  "Summarise a document I upload",
  "Draft a business plan",
];

export default function Chat() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [conv, setConv] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [category, setCategory] = useState("smart");
  const [categories, setCategories] = useState([]);
  const [providerMsg, setProviderMsg] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollRef = useRef(null);
  const ctrlRef = useRef(null);
  const asstIdRef = useRef(null);
  const justCreatedRef = useRef(null);
  const isNew = !id || id === "new";

  useEffect(() => {
    api.get("/ai/status").then(({ data }) => {
      setCategories(data.chat.categories || []);
      if (!data.chat.available) setProviderMsg(data.chat.message);
    }).catch(() => {});
  }, []);

  const loadConversation = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/conversations/${cid}`);
      setConv(data.conversation);
      setMessages(data.messages);
      setCategory(data.conversation.category || "smart");
    } catch (e) {
      toast.error("Conversation not found");
      nav("/app/chats");
    }
  }, [nav]);

  useEffect(() => {
    if (isNew) {
      setMessages([]);
      setConv(null);
      return;
    }
    if (id === justCreatedRef.current) return; // don't refetch our own live session
    loadConversation(id);
  }, [id, isNew, loadConversation]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = (content, opts = {}) => {
    if (streaming) return;
    const cat = opts.category || category;
    if (opts.category) setCategory(opts.category);
    const clientMsgId = crypto.randomUUID();
    const userMsg = { id: "tmp-u", role: "user", content, status: "complete" };
    const asstTmp = { id: "tmp-a", role: "assistant", content: "", status: "streaming", category: cat };
    setMessages((m) => [...m, userMsg, asstTmp]);
    setStreaming(true);
    asstIdRef.current = "tmp-a";
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;

    streamChat(
      "/chat",
      { conversation_id: isNew ? null : id, content, client_msg_id: clientMsgId, category: cat },
      {
        onMeta: (e) => {
          asstIdRef.current = e.assistant_message_id;
          setMessages((m) => m.map((x) =>
            x.id === "tmp-u" ? { ...x, id: e.user_message_id } :
            x.id === "tmp-a" ? { ...x, id: e.assistant_message_id } : x));
          if (isNew) {
            justCreatedRef.current = e.conversation_id;
            setConv({ id: e.conversation_id, title: content.slice(0, 48) });
            nav(`/app/chat/${e.conversation_id}`, { replace: true });
          }
        },
        onToken: (d) => setMessages((m) => m.map((x) => x.id === asstIdRef.current ? { ...x, content: x.content + d } : x)),
        onDone: (e) => {
          setStreaming(false);
          setMessages((m) => m.map((x) => x.id === asstIdRef.current ? { ...x, status: "complete" } : x));
          if (e.title) setConv((c) => ({ ...(c || {}), title: e.title }));
        },
        onError: (msg) => {
          setStreaming(false);
          setMessages((m) => m.map((x) => x.id === asstIdRef.current ? { ...x, status: "error", content: msg } : x));
          toast.error(msg);
        },
      },
      ctrl.signal
    );
  };

  const autoSentRef = useRef(false);
  useEffect(() => {
    if (isNew && location.state?.prompt && !autoSentRef.current) {
      autoSentRef.current = true;
      const { prompt, category: cat } = location.state;
      nav("/app/chat/new", { replace: true, state: {} });
      setTimeout(() => send(prompt, { category: cat }), 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const stop = () => {
    ctrlRef.current?.abort();
    setStreaming(false);
  };

  const regenerate = () => {
    if (streaming || isNew) return;
    setMessages((m) => {
      const copy = [...m];
      while (copy.length && copy[copy.length - 1].role === "assistant") copy.pop();
      return [...copy, { id: "tmp-a", role: "assistant", content: "", status: "streaming", category }];
    });
    setStreaming(true);
    asstIdRef.current = "tmp-a";
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    streamChat(`/conversations/${id}/regenerate`, {}, {
      onMeta: (e) => {
        asstIdRef.current = e.assistant_message_id;
        setMessages((m) => m.map((x) => x.id === "tmp-a" ? { ...x, id: e.assistant_message_id } : x));
      },
      onToken: (d) => setMessages((m) => m.map((x) => x.id === asstIdRef.current ? { ...x, content: x.content + d } : x)),
      onDone: () => { setStreaming(false); setMessages((m) => m.map((x) => x.id === asstIdRef.current ? { ...x, status: "complete" } : x)); },
      onError: (msg) => { setStreaming(false); toast.error(msg); },
    }, ctrl.signal);
  };

  const feedback = async (mid, value) => {
    setMessages((m) => m.map((x) => x.id === mid ? { ...x, feedback: x.feedback === value ? null : value } : x));
    try { await api.post(`/messages/${mid}/feedback`, { feedback: value }); } catch (e) {}
  };

  const branch = async () => {
    try {
      const { data } = await api.post(`/conversations/${id}/duplicate`);
      toast.success("Conversation branched");
      nav(`/app/chat/${data.id}`);
    } catch (e) { toast.error("Could not branch"); }
  };

  const rename = async () => {
    const title = window.prompt("Rename conversation", conv?.title || "");
    if (!title) return;
    await api.patch(`/conversations/${id}`, { title });
    setConv((c) => ({ ...c, title }));
  };
  const archive = async () => { await api.patch(`/conversations/${id}`, { archived: true }); toast.success("Archived"); nav("/app/chats"); };
  const del = async () => { if (!window.confirm("Delete this conversation?")) return; await api.delete(`/conversations/${id}`); toast.success("Deleted"); nav("/app/chats"); };
  const exportChat = () => {
    const md = messages.map((m) => `## ${m.role === "user" ? "You" : "BDVG AI"}\n\n${m.content}`).join("\n\n---\n\n");
    const blob = new Blob([`# ${conv?.title || "BDVG AI Conversation"}\n\n${md}`], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(conv?.title || "bdvg-chat").slice(0, 40)}.md`;
    a.click();
  };
  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied to clipboard"); };

  return (
    <div className="flex h-full flex-col">
      {!isNew && (
        <header className="flex items-center justify-between border-b border-white/8 px-5 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="truncate font-heading text-sm font-medium text-white/90" data-testid="chat-title">
              {conv?.title || "Conversation"}
            </h2>
            <span className="hidden text-xs text-white/25 sm:inline">| BDVG AI</span>
          </div>
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} className="rounded-lg p-2 text-white/50 hover:bg-white/5 hover:text-white" data-testid="chat-menu">
              <MoreHorizontal className="h-5 w-5" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-xl border border-white/10 bg-black/95 p-1.5 backdrop-blur-xl" data-testid="chat-menu-list">
                  {[
                    { label: "Rename", icon: Pencil, fn: rename },
                    { label: "Branch", icon: GitBranch, fn: branch },
                    { label: "Export", icon: Download, fn: exportChat },
                    { label: "Share", icon: Share2, fn: share },
                    { label: "Archive", icon: Archive, fn: archive },
                    { label: "Delete", icon: Trash2, fn: del, danger: true },
                  ].map((it) => (
                    <button key={it.label} onClick={() => { setMenuOpen(false); it.fn(); }}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-white/5 ${it.danger ? "text-destructive" : "text-white/75"}`}
                      data-testid={`chat-action-${it.label.toLowerCase()}`}>
                      <it.icon className="h-4 w-4" /> {it.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </header>
      )}

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        {providerMsg && (
          <div className="mx-auto mt-4 flex max-w-3xl items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200/90" data-testid="provider-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {providerMsg} — connect your BDVG AI model endpoint in Settings.
          </div>
        )}

        {isNew && messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-4 text-center">
            <BrandMark size={84} glow />
            <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              What would you like to do?
            </h1>
            <p className="mt-3 text-white/45">BDVG AI figures out the right capability for your request.</p>
            <div className="mt-8 grid w-full max-w-xl grid-cols-1 gap-2.5 sm:grid-cols-2">
              {STARTERS.map((s) => (
                <button key={s} onClick={() => send(s)}
                  className="rounded-xl border border-white/10 bg-charcoal px-4 py-3 text-left text-sm text-white/70 transition-all hover:-translate-y-0.5 hover:border-gold/30 hover:text-white"
                  data-testid={`starter-${s.slice(0, 10)}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-7 px-4 py-8">
            {messages.map((m) => (
              <Message key={m.id} m={m} streaming={streaming} onCopy={() => { navigator.clipboard.writeText(m.content); toast.success("Copied"); }}
                onRegenerate={regenerate} onFeedback={feedback} onEdit={() => send(m.content)} />
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-white/8 pt-3">
        <Composer onSend={send} streaming={streaming} onStop={stop} category={category} setCategory={setCategory} categories={categories} autoFocus />
      </div>
    </div>
  );
}

function Message({ m, streaming, onCopy, onRegenerate, onFeedback, onEdit }) {
  const isUser = m.role === "user";
  if (isUser) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end" data-testid="message-user">
        <div className="group relative max-w-[85%]">
          <div className="rounded-2xl rounded-br-md bg-graphite px-4 py-2.5 text-[15px] text-white/95">{m.content}</div>
          <button onClick={onEdit} className="absolute -left-9 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100" title="Edit & resend" data-testid="message-edit">
            <Pencil className="h-3.5 w-3.5 text-white/40 hover:text-white" />
          </button>
        </div>
      </motion.div>
    );
  }
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3" data-testid="message-assistant">
      <BrandMark size={30} className="mt-1 shrink-0" />
      <div className="group min-w-0 flex-1">
        {m.status === "error" ? (
          <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-red-300">
            <AlertTriangle className="h-4 w-4" /> {m.content || "Provider unavailable"}
          </div>
        ) : m.content ? (
          <Markdown>{m.content}</Markdown>
        ) : (
          <div className="flex items-center gap-2 text-sm text-white/40"><Loader2 className="h-4 w-4 animate-spin text-gold" /> BDVG AI is thinking…</div>
        )}

        {m.status === "complete" && (
          <div className="mt-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <ActionBtn onClick={onCopy} icon={Copy} label="Copy" testid="message-copy" />
            <ActionBtn onClick={onRegenerate} icon={RefreshCw} label="Regenerate" testid="message-regenerate" disabled={streaming} />
            <ActionBtn onClick={() => onFeedback(m.id, "like")} icon={ThumbsUp} label="Like" active={m.feedback === "like"} testid="message-like" />
            <ActionBtn onClick={() => onFeedback(m.id, "dislike")} icon={ThumbsDown} label="Dislike" active={m.feedback === "dislike"} testid="message-dislike" />
          </div>
        )}
      </div>
    </motion.div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, active, disabled, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} title={label} data-testid={testid}
      className={`rounded-md p-1.5 transition-colors disabled:opacity-30 ${active ? "text-gold" : "text-white/35 hover:bg-white/5 hover:text-white"}`}>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
